/** Shared Loops wordmark — used for Order Agent until a dedicated mark exists. */
export const LOOPS_LOGO_SRC = "/images/loops.svg";

export const ORDER_AGENT_LOGO = {
  src: LOOPS_LOGO_SRC,
  alt: "Order Agent",
};

const ORDER_AGENT_KEYS = {
  CHAT_AGENT: ORDER_AGENT_LOGO,
  "CHAT AGENT": ORDER_AGENT_LOGO,
  "ORDER AGENT": ORDER_AGENT_LOGO,
  ORDER_AGENT: ORDER_AGENT_LOGO,
  "AI ORDER AGENT": { ...ORDER_AGENT_LOGO, alt: "AI Order Agent" },
};

/** Rectangular badge marks for product pricing rows (Figma custom-price panel). */
export const PLATFORM_BADGE_LOGOS = {
  JAHEZ: { src: "/images/jahez-badge.png", alt: "Jahez", corners: "square" },
  KEETA: { src: "/images/keeta-badge.png", alt: "Keeta", corners: "square" },
  NINJA: { src: "/images/ninja-badge.png", alt: "Ninja", corners: "rounded" },
  CHEFZ: { src: "/images/chefz-badge.png", alt: "The Chefz", corners: "square" },
  FOODICS: { src: "/images/foodics.svg", alt: "Foodics", corners: "square" },
};

/** Local brand marks — used when API/remote logo URLs are missing or broken. */
export const PLATFORM_LOGO_ASSETS = {
  JAHEZ: { src: "/images/jahez-new-logo.svg", alt: "Jahez" },
  KEETA: { src: "/images/keeta.svg", alt: "Keeta" },
  CHEFZ: { src: "/images/the_chefz_logo.svg", alt: "The Chefz" },
  NINJA: { src: "/images/ninga.svg", alt: "Ninja" },
  FOODICS: { src: "/images/foodics.svg", alt: "Foodics" },
  TABSENSE: { src: "/images/tabsense-icon.svg", alt: "TabSense" },
  TRACK_HUB: { src: LOOPS_LOGO_SRC, alt: "TrackHub" },
  NOON: { src: "/images/noon-icon.svg", alt: "Noon" },
  ORDER_AGENT: ORDER_AGENT_LOGO,
  DIGITAL_CATALOG: { src: LOOPS_LOGO_SRC, alt: "Digital Catalog" },
};

export function normalizePlatformKey(value = "") {
  return String(value)
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isOrderAgentPlatform(value = "") {
  const upper = normalizePlatformKey(value);
  return upper.includes("CHAT AGENT") || upper.includes("ORDER AGENT");
}

function nameParts(appOrName) {
  if (!appOrName) return [];
  if (typeof appOrName === "string") return [appOrName];
  return [
    appOrName.application,
    appOrName.name,
    appOrName.name_en,
    appOrName.appType,
    appOrName.app,
    appOrName.key,
  ].filter(Boolean);
}

export function isOrderAgentApp(appOrName = "") {
  if (appOrName && typeof appOrName === "object") {
    return nameParts(appOrName).some(isOrderAgentPlatform);
  }
  return isOrderAgentPlatform(appOrName);
}

export function isDigitalCatalogPlatform(value = "") {
  const upper = normalizePlatformKey(value);
  return (
    upper.includes("DIGITAL CATALOG") ||
    upper === "CATALOG" ||
    upper === "DIGITAL_CATALOG"
  );
}

export function isDigitalCatalogApp(appOrName = "") {
  if (appOrName && typeof appOrName === "object") {
    if (appOrName.appType && normalizePlatformKey(appOrName.appType).includes("CATALOG")) {
      return true;
    }
    return nameParts(appOrName).some(isDigitalCatalogPlatform);
  }
  return isDigitalCatalogPlatform(appOrName);
}

export function isTrackHubPlatform(value = "") {
  const upper = normalizePlatformKey(value);
  return upper.includes("TRACK HUB") || upper.includes("TRACKHUB");
}

export function isTrackHubApp(appOrName = "") {
  if (appOrName && typeof appOrName === "object") {
    if (appOrName.appType && isTrackHubPlatform(appOrName.appType)) {
      return true;
    }
    return nameParts(appOrName).some(isTrackHubPlatform);
  }
  return isTrackHubPlatform(appOrName);
}

/** Wordmark-style logos (Loops) need a wider frame on integration cards. */
export function usesWideIntegrationLogo(appOrName = "") {
  return isOrderAgentApp(appOrName) || isDigitalCatalogApp(appOrName) || isTrackHubApp(appOrName);
}

function matchPlatformAssetKey(normalized) {
  if (!normalized) return null;
  if (normalized.includes("CHAT AGENT") || normalized.includes("ORDER AGENT")) {
    return "ORDER_AGENT";
  }
  if (normalized.includes("CATALOG")) return "DIGITAL_CATALOG";
  if (normalized.includes("FOODICS")) return "FOODICS";
  if (normalized.includes("NINJA")) return "NINJA";
  if (normalized.includes("JAHEZ")) return "JAHEZ";
  if (normalized.includes("KEETA")) return "KEETA";
  if (normalized.includes("CHEFZ")) return "CHEFZ";
  if (normalized.includes("TAB SENSE") || normalized.includes("TABSENSE")) {
    return "TABSENSE";
  }
  if (normalized.includes("TRACK HUB") || normalized.includes("TRACKHUB")) {
    return "TRACK_HUB";
  }
  if (normalized.includes("NOON")) return "NOON";
  return null;
}

function resolveFromAssets(appOrName, assets, { height } = {}) {
  for (const part of nameParts(appOrName)) {
    const key = matchPlatformAssetKey(normalizePlatformKey(part));
    if (key && assets[key]) {
      const logo = assets[key];
      return height ? { ...logo, height } : logo;
    }
  }
  return null;
}

/** Resolve a local platform logo from an app object or display name. */
export function resolvePlatformLogo(appOrName, { height } = {}) {
  return resolveFromAssets(appOrName, PLATFORM_LOGO_ASSETS, { height });
}

/** Badge-style logos for pricing rows; falls back to standard marks. */
export function resolvePlatformBadgeLogo(appOrName, { height } = {}) {
  return (
    resolveFromAssets(appOrName, PLATFORM_BADGE_LOGOS, { height }) ||
    resolvePlatformLogo(appOrName, { height })
  );
}

/** Same marks as apps-management (`resolvePlatformLogo`); 40×40 container applied in UI. */
export function resolveOrderPlatformLogo(appOrName, options = {}) {
  return resolvePlatformLogo(appOrName, options);
}

/** Spread into per-page platform logo maps (optional height for img style). */
export function orderAgentLogoEntries(height) {
  const withHeight = height
    ? Object.fromEntries(
        Object.entries(ORDER_AGENT_KEYS).map(([key, logo]) => [
          key,
          { ...logo, height },
        ])
      )
    : ORDER_AGENT_KEYS;
  return withHeight;
}

/** Prefer bundled logos so cards/rows still render when remote URLs 404. */
export function resolveIntegrationLogo(app) {
  const local = resolvePlatformLogo(app);
  if (local?.src) return local.src;
  return app?.logo || "";
}

/** Swap a broken remote img to the local mark, then hide if that also fails. */
export function handlePlatformLogoError(event, appOrName, { badge } = {}) {
  const img = event.currentTarget;
  if (!img) return;
  if (badge && !img.dataset.logoBadgeFallback) {
    const standard = resolvePlatformLogo(appOrName)?.src;
    if (standard && img.src !== standard) {
      img.dataset.logoBadgeFallback = "1";
      img.src = standard;
      return;
    }
  }
  const fallback = (badge ? resolvePlatformBadgeLogo : resolvePlatformLogo)(appOrName)?.src;
  if (fallback && !img.dataset.logoFallback) {
    img.dataset.logoFallback = "1";
    img.src = fallback;
    return;
  }
  img.style.display = "none";
}
